import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ShieldCheck, ShieldX, Lock as LockIcon, Unlock as UnlockIcon } from "lucide-react";
import { toast } from "sonner";
import { fmtKES, photoUrl } from "@/lib/format";

export default function TeamsPage() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRetainFor, setShowRetainFor] = useState(null);
  const [form, setForm] = useState({ name: "", tier: "new", owner_name: "", carry_over_balance: 0, slug: "" });
  const [validations, setValidations] = useState({});
  const canManage = ["super_admin", "league_admin"].includes(user.role);

  const load = async () => {
    const [tr, pr] = await Promise.all([api.get("/teams"), api.get("/players")]);
    setTeams(tr.data); setPlayers(pr.data);
    // Validate rosters in parallel
    const v = {};
    await Promise.all(tr.data.map(async (t) => {
      try {
        const r = await api.post(`/teams/${t.id}/validate-roster`, {});
        v[t.id] = r.data;
      } catch {} /* eslint-disable-line no-empty */
    }));
    setValidations(v);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      await api.post("/teams", form);
      toast.success("Team created");
      setForm({ name: "", tier: "new", owner_name: "", carry_over_balance: 0, slug: "" });
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete team?")) return;
    await api.delete(`/teams/${id}`);
    load();
  };

  const retain = async (playerId, teamId) => {
    try {
      const r = await api.post(`/players/${playerId}/retain`, { team_id: teamId });
      toast.success(`Retained for KES ${r.data.cost.toLocaleString()}`);
      setShowRetainFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unretain = async (playerId) => {
    try { await api.post(`/players/${playerId}/unretain`, {}); toast.success("Released"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Franchises</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Teams</h1>
        </div>
        {canManage && (
          <Button data-testid="btn-add-team" onClick={() => setShowForm(!showForm)} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold">
            <Plus className="w-4 h-4 mr-1.5" /> Add Team
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-6 bg-[#11241b] border-white/10 mb-6" data-testid="team-form">
          <div className="grid md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Team Name</label>
              <Input data-testid="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tier</label>
              <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                <SelectTrigger data-testid="team-tier" className="bg-[#060d09] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white">
                  <SelectItem value="s1">Season 1 (Returning)</SelectItem>
                  <SelectItem value="s2">Season 2 (Returning)</SelectItem>
                  <SelectItem value="new">New Season 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Owner Name</label>
              <Input data-testid="team-owner" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Carry-Over Balance (KES)</label>
              <Input data-testid="team-carry" type="number" value={form.carry_over_balance} onChange={(e) => setForm({ ...form, carry_over_balance: parseFloat(e.target.value) || 0 })} className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Public URL slug (optional)</label>
              <Input data-testid="team-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="e.g. lobsters" className="bg-[#060d09] border-white/10 text-white mt-1" />
              <div className="text-[10px] text-zinc-500 mt-1">Public page will be <span className="text-zinc-300">/team/{form.slug || "—"}</span></div>
            </div>
            <Button data-testid="btn-create-team" onClick={create} className="bg-[#00A86B] text-white font-bold">Create</Button>
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {teams.map((t) => {
          const v = validations[t.id];
          return (
            <Card key={t.id} className="p-5 bg-[#11241b] border-white/10" data-testid={`team-card-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{t.tier?.toUpperCase()} · Owner: {t.owner_name || "—"}</div>
                  <div className="font-anton text-3xl uppercase text-white tracking-tight leading-none">{t.name}</div>
                  {t.slug && (
                    <a href={`/team/${t.slug}`} target="_blank" rel="noreferrer" data-testid={`team-public-url-${t.id}`} className="text-[10px] uppercase tracking-widest text-[#00A86B] hover:underline mt-1 inline-block">
                      /team/{t.slug} ↗
                    </a>
                  )}
                </div>
                {canManage && (
                  <Button data-testid={`btn-delete-team-${t.id}`} size="sm" variant="outline" onClick={() => del(t.id)} className="border-red-500/30 text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                <div>
                  <div className="text-zinc-500 text-[10px] uppercase tracking-widest">Purse Remaining</div>
                  <div className="font-mono-score text-[#00A86B] text-2xl">{fmtKES(t.purse_remaining)}</div>
                  <div className="text-[10px] text-zinc-500">of {fmtKES(t.purse_initial)}</div>
                </div>
                <div>
                  <div className="text-zinc-500 text-[10px] uppercase tracking-widest">Players</div>
                  <div className="font-mono-score text-white text-2xl">{t.players?.length || 0} / 7</div>
                  {v && (
                    <div className={`text-[10px] mt-0.5 ${v.valid ? "text-emerald-400" : "text-amber-400"}`}>
                      {v.valid ? <ShieldCheck className="inline w-3 h-3 mr-0.5" /> : <ShieldX className="inline w-3 h-3 mr-0.5" />}
                      Intl {v.stats?.international} · Local {v.stats?.local} · Cat A/B {v.stats?.cat_ab} · Cat C {v.stats?.cat_c}
                    </div>
                  )}
                </div>
              </div>
              {v?.issues?.length > 0 && (
                <div className="text-xs text-amber-400 mb-3">⚠ {v.issues.join(" · ")}</div>
              )}
              <div className="flex gap-1 flex-wrap">
                {(t.players || []).map((p) => (
                  <div key={p.id} className="relative w-9 h-9 rounded-full overflow-hidden bg-zinc-800 border border-white/10" title={`${p.name}${p.is_retained ? " (retained)" : ""}`}>
                    {p.photo_path ? (
                      <img src={photoUrl(p.photo_path)} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] text-zinc-500">{p.name?.[0]}</div>
                    )}
                    {p.is_retained && <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#00A86B] text-white flex items-center justify-center text-[8px] font-bold border border-zinc-900">R</span>}
                  </div>
                ))}
                {(!t.players || t.players.length === 0) && (
                  <div className="text-xs text-zinc-600">No players drafted yet</div>
                )}
              </div>
              {canManage && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <Button data-testid={`btn-retain-${t.id}`} size="sm" variant="outline" onClick={() => setShowRetainFor(showRetainFor === t.id ? null : t.id)} className="border-[#00A86B]/30 text-[#00A86B]">
                    <LockIcon className="w-3 h-3 mr-1.5" /> Retain Player (Pre-Auction)
                  </Button>
                  {showRetainFor === t.id && (
                    <div className="mt-2 p-3 rounded-lg bg-[#060d09] border border-white/10" data-testid={`retain-panel-${t.id}`}>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Eligible (unsold) players</div>
                      <div className="max-h-44 overflow-y-auto space-y-1">
                        {players.filter((p) => !p.sold).map((p) => (
                          <button key={p.id} onClick={() => retain(p.id, t.id)} data-testid={`retain-pick-${p.id}`}
                            className="w-full text-left px-2 py-1.5 rounded hover:bg-zinc-800 text-sm flex items-center gap-2 text-white">
                            <span className="w-6 h-6 rounded-full overflow-hidden bg-zinc-800 shrink-0">
                              {p.photo_path && <img src={photoUrl(p.photo_path)} className="w-full h-full object-cover" />}
                            </span>
                            <span className="truncate flex-1">{p.name}</span>
                            <span className="text-[10px] text-zinc-500">{p.rating}★ · {p.tier?.toUpperCase()}</span>
                          </button>
                        ))}
                        {players.filter((p) => !p.sold).length === 0 && <div className="text-xs text-zinc-600">No eligible players.</div>}
                      </div>
                    </div>
                  )}
                  {(t.players || []).some((p) => p.is_retained) && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(t.players || []).filter((p) => p.is_retained).map((p) => (
                        <button key={p.id} data-testid={`unretain-${p.id}`} onClick={() => unretain(p.id)} className="text-[10px] px-2 py-1 bg-red-500/10 border border-red-500/30 text-red-400 rounded inline-flex items-center gap-1">
                          <UnlockIcon className="w-2.5 h-2.5" /> Release {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {teams.length === 0 && (
          <div className="col-span-full text-center text-zinc-500 py-12" data-testid="teams-empty">No teams yet.</div>
        )}
      </div>
    </div>
  );
}
